import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useSession } from '@/lib/SessionContext';
import { checkIsDev, checkIsOwner, getPageAccess } from '@/lib/AuthSession';
import { useClientPOs } from '@/hooks/useClientPOs';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import BrandSearchDropdown from '@/Components/shared/BrandSearchDropdown';
import { 
  Database, Search, Plus, Trash2, Pencil, ShieldAlert, Loader2, CheckCircle2, KeyRound, X,
  ChevronRight, ArrowLeft, CalendarDays, Calendar, MapPin, Phone, Mail, Building2, UserCircle2, Wallet, Tags, ChevronUp,
  CreditCard, Landmark, Check, Percent, Receipt
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

const getBrandColor = (brandName, customColor) => {
  if (customColor) return customColor;
  const colors = [
    'from-slate-700 to-slate-900',
    'from-blue-600 to-indigo-800',
    'from-emerald-600 to-teal-800',
    'from-rose-600 to-pink-800',
    'from-amber-600 to-orange-800',
    'from-purple-600 to-fuchsia-800'
  ];
  if (!brandName) return colors[0];
  const hash = Array.from(brandName).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

const getInitials = (name) => {
  if (!name) return 'B';
  return name.substring(0, 2).toUpperCase();
};

export default function WorkspaceClients() {
  const { member: currentMember } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { accountsMap: bankAccountsMap = {} } = useBankAccounts();

  // Guard access based on page_access config
  useEffect(() => {
    if (!currentMember) return;
    const access = getPageAccess(currentMember, 'klien_terverifikasi');
    const isAdminOrPrivileged = checkIsDev(currentMember) || checkIsOwner(currentMember) || currentMember.role === 'admin';
    if (access === 'none' && !isAdminOrPrivileged) {
      navigate('/office');
    }
  }, [currentMember, navigate]);

  // States
  const [selectedClient, setSelectedClient] = useState(null);
  const [activeClientTab, setActiveClientTab] = useState('terverifikasi');
  const [clientSearch, setClientSearch] = useState('');
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  useEffect(() => { setCurrentPage(1); }, [activeClientTab, clientSearch]);

  // Inline Edit States
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [searchBrand, setSearchBrand] = useState('');
  
  // Modal States
  const [simulatingClient, setSimulatingClient] = useState(null);
  const [isVerifyingState, setIsVerifyingState] = useState(false);
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  
  const [clientForm, setClientForm] = useState({
    brand_name: '',
    company_name: '',
    client_type: 'Perusahaan',
    npwp: '',
    owner_name: '',
    pic_name: '',
    email: '',
    phone: '',
    address_full: '',
    location: '',
    credit_limit: 0,
    theme_color: '',
    assigned_brands: [],
    password: '',
  });

  // Data
  const { data: clientAccounts = [], isLoading: isLoadingClients } = useQuery({
    queryKey: ['client-accounts'],
    queryFn: () => base44.entities.ClientAccount.list('-created_date')
  });

  const { data: teamMembers = [] } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => base44.entities.TeamMember.list()
  });

  const { data: dbBrandsRaw = [] } = useQuery({ queryKey: ['racikan-database'], queryFn: () => base44.entities.RacikanDatabase.list() });
  const { data: dbBahanRaw = [] } = useQuery({ queryKey: ['database-bahan'], queryFn: () => base44.entities.DatabaseBahan.list() });

  const allAvailableBrands = useMemo(() => {
    const brands = new Set();
    clientAccounts.forEach(c => { if (c.brand_name) brands.add(c.brand_name); });
    dbBrandsRaw.forEach(b => { if (b.nama_brand) brands.add(b.nama_brand); });
    dbBahanRaw.forEach(b => { if (b.nama_brand) brands.add(b.nama_brand); });
    return Array.from(brands).sort();
  }, [clientAccounts, dbBrandsRaw, dbBahanRaw]);

  const { clientPOs } = useClientPOs();

  // Enrich Data with PO History AND UNVERIFIED CLIENTS FROM POs
  const enrichedClients = useMemo(() => {
    const clientMap = new Map();
    
    // 1. Existing clients in ClientAccount DB
    clientAccounts.forEach(ca => {
      if (ca.email === 'satalawebdeveloper@gmail.com') return; // Hide bypass account
      
      if (ca.brand_name) {
        clientMap.set(ca.brand_name.toLowerCase(), { ...ca, isUnverified: ca.status !== 'verified' });
      }
    });

    // 2. Virtual clients from POs
    clientPOs.forEach(po => {
      const poBrand = po.customerName ? po.customerName.split('/')[0].trim() : '';
      if (poBrand && !clientMap.has(poBrand.toLowerCase())) {
        clientMap.set(poBrand.toLowerCase(), {
          id: `unverified-${poBrand}`,
          brand_name: poBrand,
          status: 'unverified',
          isUnverified: true,
          created_date: po.date || po.created_date,
          location: '-',
          owner_name: '-',
          pic_name: '-',
          phone: po.phone || '-',
          address_full: po.customerAddress || '-',
          email: po.customerEmail || '-'
        });
      }
    });

    return Array.from(clientMap.values()).map(ca => {
      const brandPOs = clientPOs.filter(po => {
        const poBrand = po.customerName ? po.customerName.split('/')[0].trim() : '';
        return poBrand.toLowerCase() === ca.brand_name.toLowerCase();
      }).sort((a, b) => new Date(b.date) - new Date(a.date));

      const totalOrders = brandPOs.length;
      const totalPcs = brandPOs.reduce((acc, curr) => acc + (curr.totalPcs || 0), 0);

      return { ...ca, totalOrders, totalPcs, poHistory: brandPOs };
    });
  }, [clientAccounts, clientPOs]);

  // Filtered
  const filteredClients = enrichedClients.filter(c => {
    const isTabMatch = activeClientTab === 'terverifikasi' ? c.status === 'verified' : c.status !== 'verified';
    if (!isTabMatch) return false;
    
    if (clientSearch) {
      const q = clientSearch.toLowerCase();
      return (c.brand_name?.toLowerCase().includes(q) || c.company_name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q));
    }
    return true;
  });

  // Mutations
  const verifyClientMutation = useMutation({
    mutationFn: async ({ id, status, assignedBrands }) => {
      return base44.entities.ClientAccount.update(id, { 
        status: status,
        assigned_brands: assignedBrands || []
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-accounts'] });
      toast.success('Status verifikasi klien diperbarui.');
      setSimulatingClient(null);
    }
  });

  const deleteClientMutation = useMutation({
    mutationFn: async (id) => base44.entities.ClientAccount.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-accounts'] });
      toast.success('Klien berhasil dihapus.');
      setSelectedClient(null);
    },
    onError: (error) => {
      console.error("Gagal menghapus klien:", error);
      toast.error('Gagal menghapus klien: ' + (error.message || 'Error tidak diketahui'));
    }
  });

  const saveClientMutation = useMutation({
    mutationFn: async (data) => {
      if (data.id) {
        return base44.entities.ClientAccount.update(data.id, data);
      } else {
        return base44.entities.ClientAccount.create({
          ...data,
          status: data.status || 'verified',
          created_date: new Date().toISOString()
        });
      }
    },
    onSuccess: (savedData) => {
      queryClient.invalidateQueries({ queryKey: ['client-accounts'] });
      toast.success('Data klien berhasil disimpan');
      setShowAddClientDialog(false);
      if (selectedClient && selectedClient.id === savedData.id) {
        setSelectedClient({ ...selectedClient, ...savedData });
      }
    },
    onError: (error) => {
      console.error("Gagal menyimpan klien:", error);
      toast.error('Gagal menyimpan data klien: ' + (error.message || 'Error tidak diketahui'));
    }
  });

  const handleConfirmEmailValidation = () => {
    setIsVerifyingState(true);
    setTimeout(() => {
      const initialBrands = simulatingClient.brand_name ? [simulatingClient.brand_name] : [];
      if (simulatingClient.id && simulatingClient.id.startsWith('unverified-')) {
         const payload = {
           brand_name: simulatingClient.brand_name,
           company_name: simulatingClient.company_name,
           owner_name: simulatingClient.owner_name,
           pic_name: simulatingClient.pic_name,
           email: simulatingClient.email,
           phone: simulatingClient.phone,
           address_full: simulatingClient.address_full,
           location: simulatingClient.location,
           credit_limit: simulatingClient.credit_limit || 0,
           theme_color: simulatingClient.theme_color,
           assigned_brands: initialBrands,
           status: 'verified'
         };
         saveClientMutation.mutate(payload, {
           onSuccess: () => {
             setIsVerifyingState(false);
             setSimulatingClient(null);
           }
         });
      } else {
         verifyClientMutation.mutate({ 
           id: simulatingClient.id, 
           status: 'verified', 
           assignedBrands: initialBrands 
         });
         setIsVerifyingState(false);
      }
    }, 1500);
  };

  const handleInlineSave = (field) => {
    let finalValue = editValue;
    if (field === 'assigned_brands' && typeof editValue === 'string') {
       finalValue = editValue.split(',').map(s => s.trim()).filter(Boolean);
    }
    if (field === 'credit_limit') {
       finalValue = Number(editValue) || 0;
    }
    if (field === 'allowed_payment_terms') {
       finalValue = Array.isArray(editValue) ? editValue : ['cash', 'tempo'];
    }
    if (field === 'tempo_duration_days') {
       finalValue = Number(editValue) || 30;
    }
    if (field === 'assigned_bank_account') {
       finalValue = editValue || 'all';
    }
    if (field === 'default_ppn_mode') {
       finalValue = editValue || 'auto';
    }
    
    if (selectedClient.id && selectedClient.id.startsWith('unverified-')) {
       const payload = {
         brand_name: selectedClient.brand_name,
         company_name: selectedClient.company_name,
         owner_name: selectedClient.owner_name,
         pic_name: selectedClient.pic_name,
         email: selectedClient.email,
         phone: selectedClient.phone,
         address_full: selectedClient.address_full,
         location: selectedClient.location,
         credit_limit: selectedClient.credit_limit,
         allowed_payment_terms: selectedClient.allowed_payment_terms || ['cash', 'tempo'],
         assigned_bank_account: selectedClient.assigned_bank_account || 'all',
         default_ppn_mode: selectedClient.default_ppn_mode || 'auto',
         theme_color: selectedClient.theme_color,
         assigned_brands: selectedClient.assigned_brands || [],
         status: 'unverified',
       };
       payload[field] = finalValue;
       saveClientMutation.mutate(payload);
    } else {
       saveClientMutation.mutate({
         id: selectedClient.id,
         [field]: finalValue
       });
    }
    setEditingField(null);
  };

  const renderEditableField = (field, label, icon, type = 'text', isTextArea = false) => {
    const isEditing = editingField === field;
    let value = selectedClient[field];
    
    if (field === 'assigned_brands' && Array.isArray(value)) {
       value = value.join(', ');
    }

    const displayValue = () => {
       if (type === 'password') {
         if (value) return '••••••••';
         if (selectedClient?.login_provider === 'google' || selectedClient?.auth_provider === 'google' || selectedClient?.email?.includes('gmail.com')) {
           return '🔵 Google OAuth (Akses Login 1-Klik Tanpa Sandi)';
         }
         return 'Belum Set Sandi (Gunakan Reset Password)';
       }
       if (field === 'credit_limit') return `Rp ${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0)}`;
       if (type === 'date' && value) {
         return new Date(value).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
       }
       if (field === 'address_full') {
          if (value) return value;
          if (selectedClient.addresses && selectedClient.addresses.length > 0) {
             return selectedClient.addresses[0].alamat_lengkap;
          }
       }
       return value || '-';
    };
    
    return (
      <div className="flex items-start gap-4 group relative py-3 px-4 hover:bg-slate-50 rounded-2xl transition-all border border-transparent hover:border-slate-100">
        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5 text-slate-500 shadow-inner">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1">{label}</p>
          {isEditing ? (
             <div className="flex items-start gap-2 mt-1">
               {field === 'assigned_brands' ? (
                 <div className="flex-1 space-y-2">
                   <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 border-2 border-[#8C6208]/30 rounded-xl min-h-[44px]">
                     {(typeof editValue === 'string' ? editValue.split(',').map(s=>s.trim()).filter(Boolean) : editValue || []).map((brand, idx) => (
                       <Badge key={idx} className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200 text-xs px-2 py-1 flex items-center gap-1">
                         {brand}
                         <button onClick={(e) => {
                           e.stopPropagation();
                           setEditValue(prev => {
                             const arr = (typeof prev === 'string' ? prev.split(',') : prev).map(s=>typeof s==='string'?s.trim():s).filter(Boolean);
                             return arr.filter((_, i) => i !== idx).join(', ');
                           });
                         }} className="hover:text-red-600 focus:outline-none"><X className="w-3 h-3"/></button>
                       </Badge>
                     ))}
                     {(!editValue || (typeof editValue === 'string' && editValue.trim() === '')) && (
                       <span className="text-slate-400 text-xs py-1">Belum ada brand dipilih...</span>
                     )}
                   </div>
                   <div className="relative z-50 flex gap-2">
                     <div className="flex-1">
                       <BrandSearchDropdown 
                         brands={allAvailableBrands}
                         value={searchBrand}
                         onChange={setSearchBrand}
                         onSelect={(brand) => {
                           setEditValue(prev => {
                             const arr = (typeof prev === 'string' ? prev.split(',') : prev).map(s=>typeof s==='string'?s.trim():s).filter(Boolean);
                             if (!arr.includes(brand)) arr.push(brand);
                             return arr.join(', ');
                           });
                           setSearchBrand('');
                         }}
                         placeholder="Cari dan pilih brand..."
                       />
                     </div>
                     <Button 
                       type="button"
                       variant="outline"
                       onClick={() => {
                         if (searchBrand.trim()) {
                           setEditValue(prev => {
                             const arr = (typeof prev === 'string' ? prev.split(',') : prev).map(s=>typeof s==='string'?s.trim():s).filter(Boolean);
                             if (!arr.includes(searchBrand.trim())) arr.push(searchBrand.trim());
                             return arr.join(', ');
                           });
                           setSearchBrand('');
                         }
                       }}
                       className="h-[42px] px-3 shrink-0 rounded-xl border-[#8C6208]/30 hover:bg-[#8C6208] hover:text-white"
                       title="Tambahkan Brand Baru"
                     >
                       <Plus className="w-4 h-4" />
                     </Button>
                   </div>
                 </div>
                ) : field === 'addresses' ? (
                  <div className="flex-1 space-y-3 w-full">
                    {(editValue || []).map((addr, idx) => (
                      <div key={idx} className="bg-white border-2 border-[#8C6208]/30 rounded-xl p-4 relative shadow-sm">
                        <button type="button" onClick={() => {
                           const newArr = [...editValue];
                           newArr.splice(idx, 1);
                           setEditValue(newArr);
                        }} className="absolute top-2 right-3 text-rose-500 hover:text-rose-600 text-[10px] font-bold uppercase tracking-wider">Hapus</button>
                        
                        <input type="text" value={addr.penerima || ''} onChange={e => {
                           const newArr = [...editValue]; newArr[idx].penerima = e.target.value; setEditValue(newArr);
                        }} placeholder="Nama Penerima" className="w-full text-sm font-semibold border-b border-slate-200 mb-2 pb-1 focus:outline-none focus:border-[#8C6208]" />
                        
                        <input type="text" value={addr.telepon || ''} onChange={e => {
                           const newArr = [...editValue]; newArr[idx].telepon = e.target.value; setEditValue(newArr);
                        }} placeholder="No. Telepon" className="w-full text-sm font-semibold border-b border-slate-200 mb-2 pb-1 focus:outline-none focus:border-[#8C6208]" />
 
                        <textarea value={addr.alamat_lengkap || ''} onChange={e => {
                           const newArr = [...editValue]; newArr[idx].alamat_lengkap = e.target.value; setEditValue(newArr);
                        }} placeholder="Alamat Lengkap" className="w-full text-sm font-semibold border-b border-slate-200 pb-1 focus:outline-none focus:border-[#8C6208] resize-none h-12" />
                        
                        <input type="text" value={addr.kode_pos || ''} onChange={e => {
                           const newArr = [...editValue]; newArr[idx].kode_pos = e.target.value; setEditValue(newArr);
                        }} placeholder="Kode Pos (Opsional)" className="w-full text-sm font-semibold border-b border-slate-200 mt-2 pb-1 focus:outline-none focus:border-[#8C6208]" />
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" className="w-full text-xs border-[#8C6208]/30 text-[#8C6208] hover:bg-[#F9F5EC] bg-transparent rounded-xl border-dashed" onClick={() => {
                       setEditValue([...(editValue || []), { id: `addr-${Date.now()}`, penerima: '', telepon: '', alamat_lengkap: '', kode_pos: '' }]);
                    }}>+ Tambah Alamat</Button>
                  </div>
               ) : isTextArea ? (
                 <textarea 
                   autoFocus
                   value={editValue} 
                   onChange={e => setEditValue(e.target.value)} 
                   className="w-full text-sm font-semibold border-2 border-[#8C6208]/30 rounded-xl px-3 py-2 focus:outline-none focus:border-[#8C6208] min-h-[80px] bg-white shadow-sm" 
                 />
               ) : field === 'pic_name' ? (
                 <select
                   autoFocus
                   value={editValue || ''}
                   onChange={e => setEditValue(e.target.value)}
                   className="w-full text-sm font-semibold border-2 border-[#8C6208]/30 rounded-xl px-3 py-2 focus:outline-none focus:border-[#8C6208] bg-white shadow-sm"
                 >
                   <option value="">Pilih PIC (Opsional)</option>
                   {teamMembers.map(tm => (
                     <option key={tm.id} value={tm.name}>{tm.name} ({tm.role})</option>
                   ))}
                 </select>
                ) : field === 'allowed_payment_terms' ? (
                   <div className="flex flex-wrap gap-2 py-1 flex-1">
                     {[
                       { key: 'cash', label: 'Cash (DP 50% + Pelunasan 50%)' },
                       { key: 'tempo', label: 'Tempo (Kredit)' }
                     ].map((term) => {
                       const currentArr = Array.isArray(editValue) 
                         ? editValue.map(k => k === 'dp' ? 'cash' : k) 
                         : ['cash', 'tempo'];
                       const isChecked = currentArr.includes(term.key);
                       return (
                         <button
                           key={term.key}
                           type="button"
                           onClick={() => {
                             if (isChecked) {
                               if (currentArr.length > 1) {
                                 setEditValue(currentArr.filter(k => k !== term.key));
                               } else {
                                 toast.error('Minimal harus ada 1 termin pembayaran aktif');
                               }
                             } else {
                               setEditValue([...currentArr, term.key]);
                             }
                           }}
                           className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all flex items-center gap-1.5 cursor-pointer ${
                             isChecked 
                               ? 'bg-[#8C6208] text-white border-[#8C6208] shadow-xs' 
                               : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                           }`}
                         >
                           <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center text-[10px] ${isChecked ? 'bg-white text-[#8C6208]' : 'border-slate-300'}`}>
                             {isChecked && <Check className="w-3 h-3" />}
                           </span>
                           <span>{term.label}</span>
                         </button>
                       );
                     })}
                   </div>
                 ) : field === 'tempo_duration_days' ? (
                   <div className="space-y-2 py-1 flex-1">
                     <div className="flex flex-wrap gap-1.5">
                       {[
                         { days: 14, label: '14 Hari' },
                         { days: 30, label: '30 Hari (1 Bulan - Standar)' },
                         { days: 45, label: '45 Hari' },
                         { days: 60, label: '60 Hari (2 Bulan)' }
                       ].map(p => (
                         <button
                           key={p.days}
                           type="button"
                           onClick={() => setEditValue(p.days)}
                           className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                             Number(editValue) === p.days 
                               ? 'bg-[#8C6208] text-white border-[#8C6208] shadow-xs' 
                               : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                           }`}
                         >
                           {p.label}
                         </button>
                       ))}
                     </div>
                     <div className="flex items-center gap-2 max-w-xs">
                       <span className="text-xs text-slate-500 font-semibold shrink-0">Atau atur durasi:</span>
                       <input
                         type="number"
                         value={editValue || ''}
                         onChange={e => setEditValue(e.target.value)}
                         placeholder="30"
                         className="w-20 text-xs font-bold border-2 border-[#8C6208]/30 rounded-xl px-2.5 py-1.5 bg-white text-center"
                       />
                       <span className="text-xs text-slate-500 font-semibold">Hari</span>
                     </div>
                     <p className="text-[11px] text-[#8C6208] font-medium">
                       💡 Jatuh tempo dihitung setelah status pesanan selesai & barang datang di alamat klien.
                     </p>
                   </div>
                 ) : field === 'assigned_bank_account' ? (
                   <div className="space-y-3 py-1 flex-1 w-full">
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                       {/* Opsi 1: Semua Rekening Satala */}
                       <div
                         onClick={() => setEditValue('all')}
                         className={`p-3.5 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between gap-3 ${
                           (editValue === 'all' || !editValue)
                             ? 'border-[#8C6208] bg-[#F9F5EC] text-[#302621] shadow-xs ring-1 ring-[#8C6208]/30'
                             : 'border-slate-200 bg-white hover:border-[#E6D5B8] text-slate-600'
                         }`}
                       >
                         <div className="flex items-center gap-3">
                           <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                             (editValue === 'all' || !editValue) ? 'bg-[#8C6208] text-white' : 'bg-slate-100 text-slate-500'
                           }`}>
                             <Landmark className="w-4 h-4" />
                           </div>
                           <div>
                             <p className="text-xs font-black text-slate-800">Semua Rekening Satala</p>
                             <p className="text-[10px] text-slate-500 font-medium">Klien bebas memilih saat checkout</p>
                           </div>
                         </div>
                         <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                           (editValue === 'all' || !editValue) ? 'border-[#8C6208] bg-[#8C6208]' : 'border-slate-300'
                         }`}>
                           {(editValue === 'all' || !editValue) && <Check className="w-3 h-3 text-white" />}
                         </div>
                       </div>

                       {/* Opsi Rekening dari bankAccountsMap */}
                       {(Object.keys(bankAccountsMap).length > 0 ? Object.entries(bankAccountsMap) : [
                         ['bca', { nama_bank: 'BCA', name: 'Apa Saja Bisa', number: '1741359516', label: 'Saldo Operasional' }],
                         ['bni', { nama_bank: 'BCA', name: 'Satala Dermatech Essential', number: '6821535384', label: 'Saldo Utama' }],
                         ['mandiri', { nama_bank: 'BCA', name: 'Cuan Jalan Terus', number: '1741359958', label: 'Saldo Operasional' }]
                       ]).map(([key, acc]) => {
                         const isSelected = editValue === key;
                         const bName = (acc.nama_bank || 'BCA').toUpperCase();
                         const accName = acc.name || key;
                         const accNumber = acc.number || '-';
                         const accLabel = acc.label || 'Rekening Satala';

                         return (
                           <div
                             key={key}
                             onClick={() => setEditValue(key)}
                             className={`p-3.5 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between gap-3 ${
                               isSelected
                                 ? 'border-[#8C6208] bg-[#F9F5EC] text-[#302621] shadow-xs ring-1 ring-[#8C6208]/30'
                                 : 'border-slate-200 bg-white hover:border-[#E6D5B8] text-slate-600'
                             }`}
                           >
                             <div className="flex items-center gap-3 min-w-0">
                               <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                                 isSelected ? 'bg-[#8C6208] text-white' : 'bg-slate-100 text-slate-500'
                               }`}>
                                 <CreditCard className="w-4 h-4" />
                               </div>
                               <div className="min-w-0">
                                 <div className="flex items-center gap-1.5 flex-wrap">
                                   <span className="text-[10px] font-black uppercase tracking-wider bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
                                     {bName}
                                   </span>
                                   <span className="text-xs font-black truncate text-slate-800">{accName}</span>
                                 </div>
                                 <div className="flex items-center gap-2 mt-0.5">
                                   <span className="text-xs font-mono font-black text-[#8C6208] tracking-wider">{accNumber}</span>
                                   <span className="text-[9px] text-slate-400 font-medium">{accLabel}</span>
                                 </div>
                               </div>
                             </div>
                             <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                               isSelected ? 'border-[#8C6208] bg-[#8C6208]' : 'border-slate-300'
                             }`}>
                               {isSelected && <Check className="w-3 h-3 text-white" />}
                             </div>
                           </div>
                         );
                       })}
                     </div>

                     <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2.5 border-t border-slate-100">
                       <p className="text-[11px] text-[#8C6208] font-medium flex items-center gap-1">
                         <span>💡</span>
                         <span>Default: <strong>ASB</strong> jika pesanan Tempo, dan <strong>Satala Utama</strong> jika Cash.</span>
                       </p>
                       <div className="flex items-center gap-2 shrink-0">
                         <button
                           type="button"
                           onClick={() => { setEditingField(null); setSearchBrand(''); }}
                           className="px-3.5 py-1.5 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors"
                         >
                           Batal
                         </button>
                         <button
                           type="button"
                           onClick={() => handleInlineSave(field)}
                           className="px-4 py-1.5 rounded-xl text-xs font-black bg-[#8C6208] hover:bg-[#724F06] text-white flex items-center gap-1.5 shadow-sm transition-all"
                         >
                           <CheckCircle2 className="w-3.5 h-3.5" />
                           Simpan Pilihan
                         </button>
                       </div>
                     </div>
                   </div>
                ) : field === 'default_ppn_mode' ? (
                  <div className="space-y-3 py-1 flex-1 w-full">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      {[
                        {
                          key: 'auto',
                          label: 'Otomatis Ikuti Rekening',
                          desc: 'ASB → Include PPN, SDE → Exclude PPN (+11%)',
                          badge: 'Default Rekomendasi',
                          badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        },
                        {
                          key: 'include',
                          label: 'Include PPN',
                          desc: 'Harga produk sudah termasuk PPN (Klien Bersih)',
                          badge: 'Termasuk PPN',
                          badgeColor: 'bg-blue-50 text-blue-700 border-blue-200'
                        },
                        {
                          key: 'exclude',
                          label: 'Exclude PPN',
                          desc: 'PPN (+11%) ditambahkan terpisah ke tagihan invoice',
                          badge: 'Tambah PPN 11%',
                          badgeColor: 'bg-amber-50 text-amber-700 border-amber-200'
                        }
                      ].map(opt => {
                        const isSelected = (editValue === opt.key) || (!editValue && opt.key === 'auto');
                        return (
                          <div
                            key={opt.key}
                            onClick={() => setEditValue(opt.key)}
                            className={`p-3.5 rounded-2xl border-2 transition-all cursor-pointer flex flex-col justify-between gap-2.5 ${
                              isSelected
                                ? 'border-[#8C6208] bg-[#F9F5EC] text-[#302621] shadow-xs ring-1 ring-[#8C6208]/30'
                                : 'border-slate-200 bg-white hover:border-[#E6D5B8] text-slate-600'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-xs font-black text-slate-800">{opt.label}</p>
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                isSelected ? 'border-[#8C6208] bg-[#8C6208]' : 'border-slate-300'
                              }`}>
                                {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                              </div>
                            </div>
                            <p className="text-[10px] text-slate-500 font-medium leading-relaxed">{opt.desc}</p>
                            <span className={`text-[9px] font-bold px-2 py-0.5 rounded-md border w-fit ${opt.badgeColor}`}>
                              {opt.badge}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2.5 border-t border-slate-100">
                      <p className="text-[11px] text-[#8C6208] font-medium flex items-center gap-1">
                        <span>💡</span>
                        <span>Rekening <strong>ASB (Apa Saja Bisa)</strong> otomatis <strong>Include PPN</strong>, dan <strong>SDE (Satala Dermatech)</strong> otomatis <strong>Exclude PPN (+11%)</strong>.</span>
                      </p>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => setEditingField(null)}
                          className="px-3.5 py-1.5 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors"
                        >
                          Batal
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInlineSave(field)}
                          className="px-4 py-1.5 rounded-xl text-xs font-black bg-[#8C6208] hover:bg-[#724F06] text-white flex items-center gap-1.5 shadow-sm transition-all"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Simpan Pilihan
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <input 
                    autoFocus
                    type={type === 'password' ? 'text' : type}
                    value={type === 'date' && editValue ? new Date(editValue).toISOString().split('T')[0] : (editValue || '')} 
                    onChange={e => {
                      if (type === 'date') {
                        setEditValue(e.target.value ? new Date(e.target.value).toISOString() : '');
                      } else {
                        setEditValue(e.target.value);
                      }
                    }} 
                    className="w-full text-sm font-semibold border-2 border-[#8C6208]/30 rounded-xl px-3 py-2 focus:outline-none focus:border-[#8C6208] bg-white shadow-sm" 
                  />
                )}
                {field !== 'assigned_bank_account' && field !== 'default_ppn_mode' && (
                  <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => handleInlineSave(field)} className="bg-emerald-500 hover:bg-emerald-600 text-white p-2 rounded-lg shadow-sm transition-colors" title="Simpan">
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => { setEditingField(null); setSearchBrand(''); }} className="bg-slate-200 hover:bg-slate-300 text-slate-600 p-2 rounded-lg shadow-sm transition-colors" title="Batal">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
           ) : (
             <div className="flex items-center justify-between gap-4 mt-0.5">
                {field === 'assigned_brands' ? (
                  <div className="flex flex-wrap gap-1.5">
                    {(selectedClient.assigned_brands || []).length > 0 ? (
                      selectedClient.assigned_brands.map((brand, idx) => (
                        <Badge key={idx} className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs px-2.5 py-1 font-bold rounded-lg">
                          {brand}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-slate-400 italic">Belum ada akses brand</span>
                    )}
                  </div>
                ) : field === 'allowed_payment_terms' ? (
                  <div className="flex flex-wrap gap-1.5">
                    {((selectedClient.allowed_payment_terms && selectedClient.allowed_payment_terms.length > 0)
                      ? selectedClient.allowed_payment_terms 
                      : ['cash', 'tempo']
                    ).map((termKey, idx) => {
                      const labels = {
                        cash: { text: 'Cash (DP 50% + Pelunasan)', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                        dp: { text: 'Cash (DP 50%)', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                        tempo: { text: 'Tempo (Kredit)', color: 'bg-blue-50 text-blue-700 border-blue-200' }
                      };
                      const t = labels[termKey] || { text: termKey, color: 'bg-slate-50 text-slate-700 border-slate-200' };
                      return (
                        <Badge key={idx} className={`${t.color} text-xs px-2.5 py-1 font-bold rounded-lg`}>
                          {t.text}
                        </Badge>
                      );
                    })}
                  </div>
                ) : field === 'tempo_duration_days' ? (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-50 text-blue-800 border-blue-200 text-xs px-2.5 py-1 font-bold rounded-lg">
                      {selectedClient.tempo_duration_days || 30} Hari ({Number(selectedClient.tempo_duration_days || 30) === 30 ? '1 Bulan' : `${selectedClient.tempo_duration_days || 30} Hari`})
                    </Badge>
                    <span className="text-[11px] text-slate-500 font-medium">
                      (Dihitung setelah barang datang / PO selesai)
                    </span>
                  </div>
                ) : field === 'assigned_bank_account' ? (
                  <div>
                    {selectedClient.assigned_bank_account && selectedClient.assigned_bank_account !== 'all' ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="bg-[#F9F5EC] border border-[#D4AF37] px-3 py-1 rounded-xl flex items-center gap-2 shadow-xs">
                          <Landmark className="w-3.5 h-3.5 text-[#8C6208]" />
                          <span className="text-xs font-black text-[#8C6208]">
                            {(() => {
                              const acc = bankAccountsMap[selectedClient.assigned_bank_account];
                              if (acc) {
                                const bName = (acc.nama_bank || 'BCA').toUpperCase();
                                const accName = acc.name || selectedClient.assigned_bank_account;
                                return `${bName} - ${accName} (${acc.number || '-'})`;
                              }
                              const fallbackLabels = {
                                bni: 'BCA - Satala Dermatech Essential (6821535384)',
                                bca: 'BCA - Apa Saja Bisa (1741359516)',
                                mandiri: 'BCA - Cuan Jalan Terus (1741359958)'
                              };
                              return fallbackLabels[selectedClient.assigned_bank_account] || selectedClient.assigned_bank_account;
                            })()}
                          </span>
                        </div>
                        <span className="text-[10px] bg-amber-100/70 text-amber-800 font-bold px-2 py-0.5 rounded-md border border-amber-200/60">
                          Khusus Klien Ini
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-slate-700">Semua Rekening Satala (Klien Bebas Pilih)</span>
                        <span className="text-[10px] bg-emerald-50 text-emerald-700 font-bold px-2 py-0.5 rounded-md border border-emerald-200">
                          Default Otomatis: Tempo → ASB, Cash → Satala Utama
                        </span>
                      </div>
                    )}
                  </div>
                ) : field === 'default_ppn_mode' ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    {(!selectedClient.default_ppn_mode || selectedClient.default_ppn_mode === 'auto') ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 text-xs px-2.5 py-1 font-bold rounded-lg flex items-center gap-1.5 shadow-none">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                          Otomatis Sesuai Rekening
                        </Badge>
                        <span className="text-xs text-slate-500 font-medium">
                          Default Otomatis: ASB → <strong className="text-emerald-700">Include</strong>, SDE → <strong className="text-amber-700">Exclude (+11%)</strong>
                        </span>
                      </div>
                    ) : selectedClient.default_ppn_mode === 'include' ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-blue-50 text-blue-800 border-blue-200 text-xs px-2.5 py-1 font-bold rounded-lg shadow-none">
                          Include PPN
                        </Badge>
                        <span className="text-xs text-slate-500 font-medium">
                          Semua pesanan otomatis berstatus harga sudah termasuk PPN
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-amber-50 text-amber-800 border-amber-200 text-xs px-2.5 py-1 font-bold rounded-lg shadow-none">
                          Exclude PPN (+11%)
                        </Badge>
                        <span className="text-xs text-slate-500 font-medium">
                          PPN 11% ditambahkan di luar nominal harga produk
                        </span>
                      </div>
                    )}
                  </div>
                ) : field === 'addresses' ? (
                  <div className="flex-1 space-y-2 mt-1 w-full">
                    {(selectedClient.addresses && selectedClient.addresses.length > 0) ? (
                      selectedClient.addresses.map((addr, idx) => (
                        <div key={idx} className="bg-slate-50 border border-slate-100 rounded-xl p-3 relative shadow-[inset_0_1px_2px_rgba(0,0,0,0.02)] w-full">
                          <p className="text-sm font-bold text-slate-800 mb-0.5">{addr.penerima || '-'} <span className="text-slate-500 text-xs font-semibold">({addr.telepon || '-'})</span></p>
                          <p className="text-xs text-slate-600 leading-relaxed font-medium">{addr.alamat_lengkap || '-'}</p>
                          {addr.kode_pos && <p className="text-[10px] text-slate-400 mt-1 font-mono font-semibold">Kode Pos: {addr.kode_pos}</p>}
                        </div>
                      ))
                    ) : (
                      <span className="text-sm text-slate-400 italic">Belum ada alamat pengiriman</span>
                    )}
                  </div>
                ) : (
                  <p className={`text-sm font-bold ${field === 'credit_limit' ? 'text-amber-600 font-black' : 'text-slate-800'}`}>
                     {displayValue()}
                  </p>
                )}
                <button 
                  onClick={() => { 
                    setEditingField(field); 
                    let initVal = value || '';
                    if (field === 'addresses') {
                      initVal = selectedClient.addresses ? JSON.parse(JSON.stringify(selectedClient.addresses)) : [];
                      if (initVal.length === 0 && selectedClient.address_full) {
                         initVal.push({ id: `addr-${Date.now()}`, penerima: selectedClient.pic_name || selectedClient.owner_name || '', telepon: selectedClient.phone || '', alamat_lengkap: selectedClient.address_full, kode_pos: '' });
                      }
                    } else if (field === 'address_full' && !initVal && selectedClient.addresses?.length > 0) {
                      initVal = selectedClient.addresses[0].alamat_lengkap || '';
                    } else if (field === 'allowed_payment_terms') {
                      initVal = selectedClient.allowed_payment_terms || ['cash', 'tempo'];
                    } else if (field === 'tempo_duration_days') {
                      initVal = selectedClient.tempo_duration_days || 30;
                    } else if (field === 'assigned_bank_account') {
                      initVal = selectedClient.assigned_bank_account || 'all';
                    } else if (field === 'default_ppn_mode') {
                      initVal = selectedClient.default_ppn_mode || 'auto';
                    }
                    setEditValue(initVal); 
                    setSearchBrand(''); 
                  }} 
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-[#8C6208] hover:bg-[#F9F5EC] p-2 rounded-lg transition-all shrink-0"
                  title="Edit Data"
                >
                  <Pencil className="w-4 h-4" />
                </button>
             </div>
           )}
        </div>
      </div>
    );
  };

  // --- DETAIL VIEW ---
  if (selectedClient) {
    const gradientClass = getBrandColor(selectedClient.brand_name, selectedClient.theme_color);
    
    return (
      <div className="space-y-6 pb-16 text-[#302621]">
        <button 
          onClick={() => setSelectedClient(null)}
          className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-800 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Kembali ke Daftar Klien
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Sidebar Profile (No longer has PO stats or all fields) */}
          <Card className="lg:col-span-1 border-0 shadow-lg overflow-hidden rounded-[2rem] bg-slate-900 relative h-max">
            <div className={`absolute inset-0 opacity-20 bg-gradient-to-br ${gradientClass}`} />
            
            <CardContent className="p-8 relative z-10 text-white flex flex-col items-center text-center space-y-6">
              <div className="w-full flex justify-between absolute top-6 px-6">
                 {selectedClient.status === 'verified' ? (
                   <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-500/30">VERIFIED</Badge>
                 ) : (
                   <Badge className="bg-amber-500/20 text-amber-200 border-amber-500/30 cursor-pointer hover:bg-amber-500/40" onClick={() => setSimulatingClient(selectedClient)}>PENDING - KLIK VERIFIKASI</Badge>
                 )}
                 <button onClick={() => { 
                    if (selectedClient.id && selectedClient.id.startsWith('unverified-')) {
                      toast.error('Klien ini dibuat otomatis dari riwayat PO. Untuk menghapusnya, silakan hapus atau ubah data PO terkait.');
                      return;
                    }
                    if(confirm('Hapus permanen?')) deleteClientMutation.mutate(selectedClient.id); 
                  }} className="w-8 h-8 rounded-full bg-red-500/20 hover:bg-red-500/40 text-red-200 flex items-center justify-center transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
              </div>

              <div className="w-24 h-24 rounded-3xl bg-white/10  flex items-center justify-center border border-white/20 shadow-2xl mt-8">
                 <Building2 className="w-12 h-12 text-white opacity-80" />
              </div>

              <div>
                <h2 className="text-3xl font-black tracking-tight mb-2">{selectedClient.brand_name}</h2>
                <p className="text-white/60 font-medium text-sm">{selectedClient.company_name || 'PT Belum Diatur'}</p>
                <p className="text-white/40 font-mono text-xs mt-3">ID: {selectedClient.id}</p>

                {selectedClient.status === 'verified' && (
                  <div className="mt-6 flex flex-col items-center bg-white/5 p-4 rounded-2xl border border-white/10">
                    <p className="text-xs font-bold text-white/50 mb-3 uppercase tracking-wider">QR Code Klien</p>
                    <div className="bg-white p-2 rounded-xl mb-3">
                      <QRCodeCanvas 
                        id={`qr-${selectedClient.id}`}
                        value={selectedClient.id} 
                        size={120} 
                        level="H" 
                        includeMargin={false}
                      />
                    </div>
                    <Button 
                      onClick={() => {
                        const canvas = document.getElementById(`qr-${selectedClient.id}`);
                        if (canvas) {
                          const url = canvas.toDataURL('image/png');
                          const link = document.createElement('a');
                          link.download = `QR-Klien-${selectedClient.brand_name}.png`;
                          link.href = url;
                          link.click();
                        }
                      }}
                      variant="outline" 
                      className="w-full text-xs h-8 border-white/20 hover:bg-white/10 text-white bg-transparent"
                    >
                      Unduh QR Code
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Editable Data Klien */}
          <Card className="lg:col-span-2 border border-slate-200/50 shadow-sm bg-white rounded-[2rem]">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-8 pb-6 border-b border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-[#F9F5EC] flex items-center justify-center text-[#8C6208]">
                   <Database className="w-6 h-6" />
                </div>
                <div>
                   <h3 className="text-xl font-black text-slate-800 tracking-tight">Data Detail & Kemitraan</h3>
                   <p className="text-xs font-semibold text-slate-400 mt-0.5">Arahkan kursor ke baris data dan klik ikon pensil untuk mengubah informasi.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                 {renderEditableField('owner_name', 'Nama Owner', <UserCircle2 className="w-4 h-4" />)}
                 {renderEditableField('pic_name', 'Nama PIC / Penanggung Jawab', <UserCircle2 className="w-4 h-4" />)}
                 
                 {renderEditableField('client_type', 'Tipe Klien (Perusahaan/Pribadi)', <UserCircle2 className="w-4 h-4" />)}
                 {renderEditableField('npwp', 'NPWP / NIK', <Database className="w-4 h-4" />)}
                 
                 {renderEditableField('phone', 'Nomor Telepon', <Phone className="w-4 h-4" />)}
                 {renderEditableField('email', 'Email (Akses Login)', <Mail className="w-4 h-4" />)}
                 
                 {renderEditableField('location', 'Kota / Wilayah', <MapPin className="w-4 h-4" />)}
                 {renderEditableField('created_date', 'Bergabung Sejak', <CalendarDays className="w-4 h-4" />, 'date')}
                 {renderEditableField('password', 'Kata Sandi', <KeyRound className="w-4 h-4" />, 'password')}
                 
                 <div className="md:col-span-2 mt-4 pt-4 border-t border-slate-100">
                    {renderEditableField('addresses', 'Daftar Alamat Pengiriman', <MapPin className="w-4 h-4" />)}
                 </div>

                 <div className="md:col-span-2 mt-2 pt-2 border-t border-slate-100">
                    {renderEditableField('assigned_brands', 'Akses Database Brand (Pisahkan dengan Koma)', <Tags className="w-4 h-4" />)}
                 </div>
                 <div className="md:col-span-2 mt-2 pt-2 border-t border-slate-100">
                    {renderEditableField('credit_limit', 'Limit Kredit (Rp)', <Wallet className="w-4 h-4" />, 'number')}
                 </div>
                 <div className="md:col-span-2 mt-2 pt-2 border-t border-slate-100">
                    {renderEditableField('allowed_payment_terms', 'Termin Pembayaran yang Diizinkan', <CreditCard className="w-4 h-4" />)}
                 </div>
                 {((selectedClient.allowed_payment_terms || []).includes('tempo')) && (
                    <div className="md:col-span-2 mt-2 pt-2 border-t border-slate-100 bg-blue-50/40 p-3.5 rounded-2xl border border-blue-100/80">
                       {renderEditableField('tempo_duration_days', 'Durasi Jatuh Tempo (Dihitung Setelah Barang Datang / PO Selesai)', <Calendar className="w-4 h-4 text-blue-700" />)}
                    </div>
                 )}
                 <div className="md:col-span-2 mt-2 pt-2 border-t border-slate-100">
                    {renderEditableField('assigned_bank_account', 'Rekening Tujuan Transfer Ditugaskan', <Landmark className="w-4 h-4" />)}
                  </div>
                  <div className="md:col-span-2 mt-2 pt-2 border-t border-slate-100">
                    {renderEditableField('default_ppn_mode', 'Ketentuan PPN (Pajak Pertambahan Nilai)', <Receipt className="w-4 h-4" />)}
                 </div>
              </div>

            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // --- LIST VIEW ---
  
  function renderClientFormModal() {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 -xs">
        <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden border border-[#E6D5B8] flex flex-col max-h-[95vh]">
          <div className="bg-[#302621] p-5 text-white flex items-center justify-between border-b border-[#E6D5B8]/20">
            <span className="font-black text-sm text-[#FFFDF9] tracking-tight">Daftarkan Klien Baru</span>
            <button onClick={() => setShowAddClientDialog(false)} className="text-white/60 hover:text-white text-xs font-bold">Tutup</button>
          </div>
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              saveClientMutation.mutate({
                brand_name: clientForm.brand_name,
                company_name: clientForm.company_name,
                client_type: clientForm.client_type,
                npwp: clientForm.npwp,
                owner_name: clientForm.owner_name,
                pic_name: clientForm.pic_name,
                email: clientForm.email,
                phone: clientForm.phone,
                address_full: clientForm.address_full,
                location: clientForm.location,
                credit_limit: Number(clientForm.credit_limit || 0),
                assigned_brands: clientForm.assigned_brands || [],
                password: clientForm.password,
              });
            }}
            className="p-6 space-y-4 text-xs overflow-y-auto"
          >
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1">Nama Brand / Perusahaan</label>
                <Input value={clientForm.brand_name} onChange={e => setClientForm({...clientForm, brand_name: e.target.value})} placeholder="Contoh: Cielmora Skin" required className="h-10 rounded-xl" />
              </div>
              <div>
                <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1">Nama PT (Legal)</label>
                <Input value={clientForm.company_name} onChange={e => setClientForm({...clientForm, company_name: e.target.value})} placeholder="PT Cielmora Beauty" className="h-10 rounded-xl" />
              </div>
              
              <div>
                <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1">Tipe Klien</label>
                <select 
                  value={clientForm.client_type} 
                  onChange={e => setClientForm({...clientForm, client_type: e.target.value})} 
                  className="flex h-10 w-full rounded-xl border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="Perusahaan">Perusahaan</option>
                  <option value="Pribadi">Pribadi</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1">NPWP / NIK</label>
                <Input value={clientForm.npwp} onChange={e => setClientForm({...clientForm, npwp: e.target.value})} placeholder="Nomor NPWP atau NIK" className="h-10 rounded-xl" />
              </div>
              
              <div>
                <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1">Nama Owner</label>
                <Input value={clientForm.owner_name} onChange={e => setClientForm({...clientForm, owner_name: e.target.value})} placeholder="Nama Pemilik" className="h-10 rounded-xl" />
              </div>
              <div>
                <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1">Nama PIC / Penanggung Jawab</label>
                <select 
                  value={clientForm.pic_name} 
                  onChange={e => setClientForm({...clientForm, pic_name: e.target.value})} 
                  className="flex h-10 w-full rounded-xl border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Pilih PIC (Opsional)</option>
                  {teamMembers.map(tm => (
                    <option key={tm.id} value={tm.name}>{tm.name} ({tm.role})</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1">Alamat Lengkap (Utama)</label>
                <textarea 
                  value={clientForm.address_full} 
                  onChange={e => setClientForm({...clientForm, address_full: e.target.value})} 
                  placeholder="Jl. Mawar No 123..." 
                  className="flex min-h-[80px] w-full rounded-xl border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1">Kota / Wilayah</label>
                <Input value={clientForm.location} onChange={e => setClientForm({...clientForm, location: e.target.value})} placeholder="Jakarta Selatan" className="h-10 rounded-xl" />
              </div>
              <div>
                <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1">Nomor Telepon</label>
                <Input value={clientForm.phone} onChange={e => setClientForm({...clientForm, phone: e.target.value})} placeholder="08123..." className="h-10 rounded-xl" />
              </div>

              <div>
                <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1">Email Login</label>
                <Input value={clientForm.email} type="email" onChange={e => setClientForm({...clientForm, email: e.target.value})} placeholder="email@domain.com" required className="h-10 rounded-xl" />
              </div>
              <div>
                <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1">Kata Sandi</label>
                <Input type="password" value={clientForm.password} onChange={e => setClientForm({...clientForm, password: e.target.value})} placeholder="Kata Sandi" required className="h-10 rounded-xl" />
              </div>
            </div>

            <div className="pt-4 flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setShowAddClientDialog(false)} disabled={saveClientMutation.isPending}>Batal</Button>
              <Button type="submit" disabled={saveClientMutation.isPending} className="bg-[#8C6208] hover:bg-[#7A5507] text-white flex items-center gap-1.5">
                {saveClientMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {saveClientMutation.isPending ? 'Menyimpan...' : 'Buat Akun Klien'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header & Tabs */}
      <div className="flex flex-col gap-1">
        <h1 className="text-3xl font-black text-slate-800 tracking-tight flex items-center gap-2">
          <Database className="w-8 h-8 text-[#8C6208]" /> Database Klien
        </h1>
        <p className="text-xs text-slate-500">Kelola data master klien, profil, alamat, dan akses login.</p>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-2 rounded-2xl shadow-sm border border-slate-100">
        <div className="flex gap-1 bg-slate-50 p-1 rounded-xl w-full sm:w-auto overflow-x-auto">
          <button
            onClick={() => setActiveClientTab('terverifikasi')}
            className={`px-6 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${activeClientTab === 'terverifikasi' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100'}`}
          >
            Klien Terverifikasi ({enrichedClients.filter(c => c.status === 'verified').length})
          </button>
          <button
            onClick={() => setActiveClientTab('permintaan')}
            className={`px-6 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${activeClientTab === 'permintaan' ? 'bg-amber-500 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100'}`}
          >
            Permintaan Verifikasi ({enrichedClients.filter(c => c.status !== 'verified').length})
          </button>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Cari brand, nama, email..." 
              value={clientSearch}
              onChange={e => setClientSearch(e.target.value)}
              className="pl-9 h-9 text-xs rounded-xl border-slate-200 bg-slate-50 w-full"
            />
          </div>
          <Button onClick={() => { setClientForm({ brand_name: '', company_name: '', owner_name: '', pic_name: '', email: '', phone: '', address_full: '', location: '', credit_limit: 0, theme_color: '', assigned_brands: [], password: '' }); setShowAddClientDialog(true); }} className="h-9 px-4 gap-2 text-xs rounded-xl shadow-sm bg-slate-800 hover:bg-slate-700">
            <Plus className="w-4 h-4" /> Tambah Manual
          </Button>
        </div>
      </div>

      {/* Grid Elegant Cards */}
      {isLoadingClients ? (
        <div className="text-center py-12 text-[#7A695B] text-xs">Memuat data klien...</div>
      ) : filteredClients.length === 0 ? (
        <Card className="border border-[#E6D5B8] py-16 text-center bg-white rounded-2xl">
          <CardContent className="space-y-2 pt-6">
            <Database className="w-10 h-10 mx-auto text-[#A39171]/40" />
            <p className="text-sm font-bold text-[#302621]">Tidak ada mitra ditemukan</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredClients.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(c => {
            const isVerified = c.status === 'verified';
            const gradientClass = getBrandColor(c.brand_name, c.theme_color);
            
            return (
              <Card 
                key={c.id} 
                className={`border-0 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group flex flex-col ${!isVerified ? 'grayscale-[50%] opacity-90' : ''}`}
              >
                {/* Gradient Header */}
                <div className={`h-24 w-full bg-gradient-to-br ${isVerified ? gradientClass : 'from-slate-300 to-slate-400'} relative p-4 flex items-end`}>
                   {!isVerified && (
                     <Badge className="absolute top-3 right-3 bg-white/20 text-white hover:bg-white/30 border-none ">
                       PENDING
                     </Badge>
                   )}
                   <div className="absolute -bottom-6 left-4 w-12 h-12 rounded-xl bg-white shadow-md flex items-center justify-center p-1">
                      <div className={`w-full h-full rounded-lg bg-gradient-to-br ${isVerified ? gradientClass : 'from-slate-300 to-slate-400'} flex items-center justify-center`}>
                         <span className="text-white font-black text-sm">{getInitials(c.brand_name)}</span>
                      </div>
                   </div>
                </div>

                {/* Content */}
                <CardContent className="pt-8 px-5 pb-5 flex-1 flex flex-col bg-white">
                  <div className="mb-4">
                    <h3 className="font-black text-slate-800 text-base truncate" title={c.brand_name}>{c.brand_name}</h3>
                    <p className="text-[10px] text-slate-500 font-semibold truncate">{c.company_name || '-'}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-auto border-t border-slate-100 pt-4">
                    <div>
                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Nama Owner</p>
                      <p className="text-sm font-black text-slate-700 truncate">{c.owner_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Join</p>
                      <p className="text-sm font-black text-slate-700">{c.created_date ? new Date(c.created_date).getFullYear() : '-'}</p>
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <Button onClick={() => setSelectedClient(c)} variant="outline" className={`flex-1 text-xs h-8 justify-between ${isVerified ? 'text-slate-500 border-slate-200 hover:text-slate-800' : 'text-amber-600 border-amber-200 hover:bg-amber-50'}`}>
                      Lihat Detail <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                    {!isVerified && (
                      <Button onClick={(e) => { e.stopPropagation(); setSimulatingClient(c); }} className="px-3 text-xs h-8 bg-amber-500 hover:bg-amber-600 text-white shrink-0">
                        Verifikasi
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        if (c.id && c.id.startsWith('unverified-')) {
                          toast.error('Klien ini dibuat otomatis dari riwayat PO. Untuk menghapusnya, silakan hapus atau ubah data PO terkait.');
                          return;
                        }
                        if (confirm(`Yakin ingin menghapus klien "${c.brand_name}"? Data akan dipindahkan ke Keranjang Sampah.`)) {
                          deleteClientMutation.mutate(c.id);
                        }
                      }} 
                      className="px-2 text-xs h-8 border-red-200 text-red-500 hover:bg-red-50 hover:text-red-700 shrink-0"
                      title="Hapus Klien"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Pagination Controls ── */}
      {filteredClients.length > 0 && !isLoadingClients && (
        <div className="px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
            <div className="w-[80px]">
              <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 25, 50, 100].map(n => (
                    <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5">
            <button 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
            >
              &lt;
            </button>
            
            {(() => {
              const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
              const pages = [];
              for(let i=1; i<=totalPages; i++) {
                if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                        currentPage === i 
                          ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                          : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                      }`}
                    >
                      {i}
                    </button>
                  );
                } else if (i === currentPage - 2 || i === currentPage + 2) {
                  pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                }
              }
              return pages;
            })()}
            
            <button 
              onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredClients.length / itemsPerPage), p + 1))}
              disabled={currentPage >= Math.ceil(filteredClients.length / itemsPerPage)}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
            >
              &gt;
            </button>
          </div>
        </div>
      )}

      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      {/* EMAIL VERIFICATION SIMULATOR MODAL (For Pending) */}
      {simulatingClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 -xs">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-[#E6D5B8] flex flex-col">
            <div className="bg-[#302621] p-5 text-white flex items-center justify-between border-b border-[#E6D5B8]/20">
              <span className="font-black text-sm text-[#FFFDF9]">Verifikasi {simulatingClient.brand_name}</span>
              <button onClick={() => setSimulatingClient(null)} className="text-white/60 hover:text-white text-xs">Tutup</button>
            </div>
            <div className="p-6 text-center space-y-4">
              <ShieldAlert className="w-12 h-12 text-amber-500 mx-auto" />
              <p className="text-sm text-slate-700">Dengan menyetujui, Anda memvalidasi klien ini sebagai mitra resmi pabrik. Klien akan mendapatkan akses dashboard mereka.</p>
              <Button onClick={handleConfirmEmailValidation} disabled={isVerifyingState} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                {isVerifyingState ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {isVerifyingState ? 'Memverifikasi...' : 'Setujui & Verifikasi Akun'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Add Modal for NEW clients only */}
      {showAddClientDialog && renderClientFormModal()}

    </div>
  );
}
